import { apiCall } from 'modules/core/services/api'
import type { UpdateAutopilotDto } from 'openapi/models/UpdateAutopilotDto'
import { AutopilotService } from 'openapi/services/AutopilotService'
import { create } from 'zustand'
import { getBrowserTimezone } from '../../common/utils/business-timezone'
import { useBrandKitStore } from '../../creative/store/brand-kit-store'
import type {
  Autopilot,
  AutopilotChannel,
  AutopilotDirection,
  AutopilotFrequency,
  AutopilotPublishMode,
  AutopilotStatus,
} from '../types'

export type PlanField = keyof UpdateAutopilotDto

/**
 * Choices collected by the weekly-plan setup wizard (what / when / where).
 * `directions` is the multi-select set; the first entry is pinned to the
 * legacy `contentDirection` anchor for back-compat with the per-card flow.
 * `channels` is the multi-select channel set; the first entry is pinned to the
 * legacy `channel` anchor. `customDescription` feeds the `custom` direction.
 */
export interface CreatePlanInput {
  channels: AutopilotChannel[]
  directions: AutopilotDirection[]
  frequency: AutopilotFrequency
  /** Weekdays to post on (lowercase names). Authoritative schedule input. */
  preferredDays?: string[]
  /** Preferred publish time of day, "HH:mm" (24h) in the business timezone. */
  preferredTime?: string
  /** Free text for the `custom` direction (server reads contentSpecifics.description). */
  customDescription?: string
  /** Publish mode chosen in the wizard. Defaults to 'review' server-side when omitted. */
  publishMode?: AutopilotPublishMode
}

interface AutopilotState {
  autopilots: Autopilot[]
  loading: boolean
  loaded: boolean
  /** The last fetch ERRORED (loaded is still set): `autopilots: []` may be a lie, so
   *  flows that would CREATE a plan on "no plan" must not trust it (duplicate risk). */
  loadFailed: boolean
  /** Fields currently being PATCH-ed; the sheet uses this for inline spinners. */
  savingFields: PlanField[]

  fetchAutopilots: () => Promise<void>
  /**
   * Create a plan from the weekly-plan wizard's explicit choices (what / when /
   * where). Carries the browser timezone so the first slot batch fires at the
   * brand's wall clock. Refreshes the local list and returns the created plan.
   */
  createPlan: (input: CreatePlanInput) => Promise<Autopilot | null>
  /**
   * PATCH the active plan with `partial`. Optimistic local merge; on failure we
   * revert to the snapshot taken before the call. Designed for autosave-per-field
   * — pass one key at a time for the cleanest UX.
   */
  updatePlan: (id: string, partial: UpdateAutopilotDto) => Promise<Autopilot | null>
  updateStatus: (id: string, status: AutopilotStatus) => Promise<void>
  deleteAutopilot: (id: string) => Promise<void>
  clearAutopilots: () => void
}

function withSavingField(prev: PlanField[], field: PlanField): PlanField[] {
  return prev.includes(field) ? prev : [...prev, field]
}

function withoutSavingField(prev: PlanField[], field: PlanField): PlanField[] {
  return prev.filter(f => f !== field)
}

export const useAutopilotStore = create<AutopilotState>((set, get) => ({
  autopilots: [],
  loading: false,
  loaded: false,
  loadFailed: false,
  savingFields: [],

  fetchAutopilots: async () => {
    if (get().loading) return
    set({ loading: true })
    try {
      const autopilots = await apiCall(AutopilotService.autopilotControllerFindActiveV1, null)
      set({ autopilots: autopilots ?? [], loaded: true, loadFailed: false })
    } catch {
      set({ autopilots: [], loaded: true, loadFailed: true })
    } finally {
      set({ loading: false })
    }
  },

  createPlan: async ({
    channels,
    directions,
    frequency,
    preferredDays,
    preferredTime,
    customDescription,
    publishMode,
  }) => {
    set({ loading: true })
    try {
      const brandTz = useBrandKitStore.getState().brandKit.timezone
      const timezone = brandTz ?? getBrowserTimezone()
      const description = customDescription?.trim()
      const created = await apiCall(AutopilotService.autopilotControllerCreateV1, null, {
        // Pin the legacy single anchors to the first selected channel/direction;
        // the full multi-select sets live in `contentSpecifics.{channels,directions}`
        // (the rotation + fan-out sources), mirroring the defaults popover.
        channel: channels[0],
        contentDirection: directions[0],
        contentSpecifics: {
          directions,
          channels,
          lastDirectionIndex: 0,
          ...(description ? { description } : {}),
        },
        frequency,
        ...(preferredDays && preferredDays.length > 0 ? { preferredDays } : {}),
        ...(preferredTime ? { preferredTime } : {}),
        timezone,
        ...(publishMode ? { publishMode } : {}),
      })
      const list = await apiCall(AutopilotService.autopilotControllerFindActiveV1, null)
      set({ autopilots: list ?? [], loaded: true })
      return (created as Autopilot | undefined) ?? null
    } catch (err) {
      console.error('autopilot-store createPlan failed:', err)
      throw err
    } finally {
      set({ loading: false })
    }
  },

  updatePlan: async (id, partial) => {
    const fields = Object.keys(partial) as PlanField[]
    const snapshot = get().autopilots
    const target = snapshot.find(a => a.id === id)
    if (!target) return null

    set(state => ({
      autopilots: state.autopilots.map(a => (a.id === id ? { ...a, ...partial } : a)),
      savingFields: fields.reduce(withSavingField, state.savingFields),
    }))

    try {
      const updated = await apiCall(AutopilotService.autopilotControllerUpdateV1, null, id, partial)
      set(state => ({
        autopilots: state.autopilots.map(a => (a.id === id ? (updated ?? a) : a)),
        savingFields: fields.reduce(withoutSavingField, state.savingFields),
      }))
      return updated ?? null
    } catch (error) {
      set(state => ({
        autopilots: snapshot,
        savingFields: fields.reduce(withoutSavingField, state.savingFields),
      }))
      throw error
    }
  },

  updateStatus: async (id, status) => {
    const { autopilots } = get()
    const target = autopilots.find(a => a.id === id)
    if (!target) return
    set({ autopilots: autopilots.map(a => (a.id === id ? { ...a, status } : a)) })
    try {
      const updated = await apiCall(AutopilotService.autopilotControllerUpdateV1, null, id, {
        status,
      })
      set(state => ({ autopilots: state.autopilots.map(a => (a.id === id ? updated : a)) }))
    } catch (err) {
      console.error('Failed to update autopilot status:', err)
      set({ autopilots })
    }
  },

  deleteAutopilot: async (id: string) => {
    await apiCall(AutopilotService.autopilotControllerDeleteV1, null, id)
    set(state => ({ autopilots: state.autopilots.filter(a => a.id !== id) }))
  },

  clearAutopilots: () => set({ autopilots: [], loaded: false, savingFields: [] }),
}))
